import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Wrench, BarChart3 } from 'lucide-react';
import { EASING, useMagneticHover, staggerParent, staggerChild } from '../hooks/useMotion';

const differentiators = [
  {
    icon: Search,
    title: 'Business-first, not tech-first.',
    description: 'We understand your operations before recommending any AI solution.'
  },
  {
    icon: Wrench,
    title: "We build it. You don't have to.",
    description: 'Our team handles every technical detail from start to finish.'
  },
  {
    icon: BarChart3,
    title: 'We measure results, not effort.',
    description: 'More leads, fewer hours wasted, lower costs — tracked and reported.'
  }
];

const MagneticCard = ({ children, ...rest }) => {
  const m = useMagneticHover(0.15);
  return (
    <motion.div
      ref={m.ref}
      onMouseMove={m.onMouseMove}
      onMouseLeave={m.onMouseLeave}
      animate={{ x: m.x, y: m.y }}
      transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.6 }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-[#050505] overflow-hidden" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASING.appleSmooth }}
          className="max-w-3xl mx-auto text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-5" data-testid="about-label">
            Who We Are
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="about-heading">
            One team. Every layer of AI you need.
          </h2>
          <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed" data-testid="about-description">
            We design, build, and deploy AI systems that capture leads, answer calls, qualify prospects, and convert visitors — without you managing developers, vendors, or integrations.
          </p>
        </motion.div>

        <motion.div
          variants={staggerParent(0.12, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-3 gap-5"
        >
          {differentiators.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div key={item.title} variants={staggerChild(36)} className="h-full">
                <MagneticCard
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 240, damping: 18 }}
                  className="group relative bg-[#0A0A0A] rounded-2xl p-7 border border-white/[0.08] hover:border-[#FF6B00]/30 transition-colors duration-500 card-glow-hover h-full overflow-hidden"
                  data-testid={`differentiator-card-${index}`}
                >
                  {/* Glow on hover */}
                  <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{
                    background: 'radial-gradient(circle, rgba(255,107,0,0.22) 0%, transparent 70%)'
                  }} />
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/25 group-hover:bg-[#FF6B00]/25 group-hover:border-[#FF6B00]/50 flex items-center justify-center mb-5 transition-all duration-500">
                      <Icon className="w-5 h-5 text-[#FF6B00]" />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">{item.description}</p>
                  </div>
                </MagneticCard>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
